import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Spinner,
  Alert,
  Badge,
  Form,
  Modal
} from 'react-bootstrap';
import {
  FaClock,
  FaMapMarkerAlt,
  FaCalendarCheck,
  FaCalendarTimes,
  FaVideo,
  FaEye,
  FaExclamationCircle,
  FaCheck,
  FaTimes,
  FaChevronDown,
  FaChevronUp,
  FaCheckCircle,
  FaCalendarAlt,
  FaList,
  FaChevronLeft,
  FaChevronRight
} from 'react-icons/fa';
import moment from 'moment';
import apiService from '../../services/ApiServices';
import './calendarStyles.css';
import LoadingOverlay from '../shared/LoadingOverlay';
import useLoading from '../shared/useLoading';

// --- Helpers ---

const getStatusMeta = (status) => {
  const normalized = (status || '').toLowerCase();
  switch (normalized) {
    case 'approved':
    case 'confirmed':
      return { variant: 'success', label: 'Approved' };
    case 'pending':
    case 'awaiting':
    case 'pending_approval':
      return { variant: 'warning', label: 'Pending' };
    case 'rejected':
    case 'declined':
      return { variant: 'danger', label: 'Rejected' };
    case 'cancelled':
      return { variant: 'dark', label: 'Cancelled' };
    default:
      return { variant: 'secondary', label: status || 'Unknown' };
  }
};
const decodeJWT = (token) => {
  try {
    const payload = token.split(".")[1];
    const decoded = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(decoded);
  } catch (error) {
    console.error("Invalid JWT:", error);
    return null;
  }
};

const getIndicatorClass = (status) => {
  const normalized = (status || '').toLowerCase();
  if (['approved', 'confirmed'].includes(normalized)) return 'approved';
  if (['pending', 'awaiting', 'pending_approval'].includes(normalized)) return 'pending';
  if (['rejected', 'declined'].includes(normalized)) return 'rejected';
  if (normalized === 'cancelled') return 'cancelled';
  return 'default';
};

// --- Main Component ---

const MeetingList = () => {
  // Existing state
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [decisionLoading, setDecisionLoading] = useState(null);
  const [remarksState, setRemarksState] = useState({});
  const [remarksLoading, setRemarksLoading] = useState(null);
  const [remarksOpen, setRemarksOpen] = useState({});
  const [cancelModalMeeting, setCancelModalMeeting] = useState(null);
  const [cancelLoading, setCancelLoading] = useState(false);
  const { isLoading: isOverlayLoading, withLoading } = useLoading();

  // New calendar state
  const [viewMode, setViewMode] = useState('calendar'); // 'calendar' | 'list'
  const [currentMonth, setCurrentMonth] = useState(moment().startOf('month'));
  const [showDayModal, setShowDayModal] = useState(false);
  const [selectedDateKey, setSelectedDateKey] = useState(null);

  // --- Existing API & Business Logic (100% preserved) ---

  const fetchMeetings = withLoading(async () => {
    try {
      setLoading(true);
      const response = await apiService.get('/api/meeting/all-booking');
      const fetchedMeetings = response.meeting || [];
      setMeetings(fetchedMeetings);
      const initialRemarks = {};
      fetchedMeetings.forEach(m => {
        const id = m.meeting_id || m.id;
        if (id) {
          initialRemarks[id] = {
            text: m.remarks || '',
            isSeen: Boolean(m.is_seen)
          };
        }
      });
      setRemarksState(initialRemarks);
      const initialOpenState = {};
      fetchedMeetings.forEach(m => {
        const id = m.meeting_id || m.id;
        if (id) {
          initialOpenState[id] = false;
        }
      });
      setRemarksOpen(initialOpenState);
      setLoading(false);
    } catch (err) {
      setError(err.message || 'Failed to fetch meetings.');
      setLoading(false);
    }
  });

  useEffect(() => {
    fetchMeetings();
  }, []);

  const handleMeetingDecision = withLoading(async (meeting, decision) => {
    // const schema = meeting.schema || meeting.schema_name || meeting.schemaname;
    const meetingId = meeting.meeting_id || meeting.id;
    const token = localStorage.getItem("AUTH_TOKEN_KEY")
    let schema = null;
    if (token) {
      const user = decodeJWT(token);
      schema = user.schema;
      console.log(schema);
    }
    if (!schema || !meetingId) {
      alert('Missing schema or meeting id for this meeting.');
      return;
    }

    try {
      setDecisionLoading(`${meetingId}-${decision}`);
      await apiService.post(`/api/booking-page/meeting/${schema}/${meetingId}/decision`, {
        decision
      });

      alert(`Meeting ${decision}d successfully.`);
    } catch (err) {
      console.error('Error sending decision:', err);
      alert('Failed to send decision for this meeting.');
    } finally {
      await fetchMeetings();
      setDecisionLoading(null);
    }
  });

  const handleRemarkChange = (meeting, field, value) => {
    const meetingId = meeting.meeting_id || meeting.id;
    setRemarksState(prev => {
      const existing = prev[meetingId] || { text: meeting.remarks || '', isSeen: Boolean(meeting.is_seen) };
      return {
        ...prev,
        [meetingId]: {
          ...existing,
          [field]: value
        }
      };
    });
  };

  const handleRemarkSave = withLoading(async (meeting) => {
    const meetingId = meeting.meeting_id || meeting.id;
    const current = remarksState[meetingId] || { text: meeting.remarks || '', isSeen: Boolean(meeting.is_seen) };
    if (!meetingId) {
      alert('Missing meeting id for remarks.');
      return;
    }
    try {
      setRemarksLoading(meetingId);
      await apiService.patch(`/api/meeting/remarks/${meetingId}`, {
        remarks: current.text,
        is_seen: current.isSeen
      });
      await fetchMeetings();
      alert('Remarks saved.');
    } catch (err) {
      console.error('Error saving remarks:', err);
      alert('Failed to save remarks.');
    } finally {
      setRemarksLoading(null);
    }
  });

  const handleCancelMeeting = withLoading(async () => {
    if (!cancelModalMeeting) return;
    const meetingId = cancelModalMeeting.meeting_id || cancelModalMeeting.id;
    try {
      setCancelLoading(true);
      await apiService.patch(`/api/meeting/cancel/${meetingId}`);
      setCancelModalMeeting(null);
      await fetchMeetings();
      alert('Meeting cancelled successfully.');
    } catch (err) {
      console.error('Error cancelling meeting:', err);
      alert('Failed to cancel meeting.');
    } finally {
      setCancelLoading(false);
    }
  });

  // --- Memoized Data ---

  const categorizeMeetings = (meetingList) => {
    const now = moment();
    const upcomingMeetings = [];
    const pastMeetings = [];

    meetingList.forEach((meeting) => {
      const meetingStartTime = moment(meeting.starttime);
      if (meetingStartTime.isAfter(now)) {
        upcomingMeetings.push(meeting);
      } else {
        pastMeetings.push(meeting);
      }
    });

    upcomingMeetings.sort((a, b) => moment(a.starttime).diff(moment(b.starttime)));
    pastMeetings.sort((a, b) => moment(b.starttime).diff(moment(a.starttime)));

    return { upcomingMeetings, pastMeetings };
  };

  const { upcomingMeetings, pastMeetings } = categorizeMeetings(meetings);

  // Group meetings by date key (YYYY-MM-DD) — memoized
  const meetingsByDate = useMemo(() => {
    const grouped = {};
    meetings.forEach(m => {
      if (!m.starttime) return;
      const dateKey = moment(m.starttime).format('YYYY-MM-DD');
      if (!grouped[dateKey]) grouped[dateKey] = [];
      grouped[dateKey].push(m);
    });
    // Sort each day's meetings by starttime
    Object.keys(grouped).forEach(key => {
      grouped[key].sort((a, b) => moment(a.starttime).diff(moment(b.starttime)));
    });
    return grouped;
  }, [meetings]);

  // --- Calendar Navigation ---

  const goToPrevMonth = useCallback(() => {
    setCurrentMonth(prev => prev.clone().subtract(1, 'month'));
  }, []);

  const goToNextMonth = useCallback(() => {
    setCurrentMonth(prev => prev.clone().add(1, 'month'));
  }, []);

  const goToToday = useCallback(() => {
    setCurrentMonth(moment().startOf('month'));
  }, []);

  // --- Date Click Handler ---

  const handleDateClick = useCallback((dateKey, dateMeetings) => {
    if (!dateMeetings || dateMeetings.length === 0) return;
    setSelectedDateKey(dateKey);
    setShowDayModal(true);
  }, []);

  const closeDayModal = useCallback(() => {
    setShowDayModal(false);
    setSelectedDateKey(null);
  }, []);

  // Derive modal meetings from the main meetings state so they always reflect the latest data
  const selectedDateMeetings = selectedDateKey ? (meetingsByDate[selectedDateKey] || []) : [];
  const selectedDateLabel = selectedDateKey ? moment(selectedDateKey).format('dddd, MMMM D, YYYY') : '';

  // --- Existing MeetingCard Component (100% preserved) ---

  const MeetingCard = ({ meeting, isPast = false }) => (
    <Card className={`mb-3 border-0 ${isPast ? 'bg-light' : 'bg-white'} shadow-sm`}>
      <Card.Body className="p-3">
        <Row className="align-items-start">
          <Col xs={12} lg={8}>
            {/* Meeting Title */}
            <div className="d-flex align-items-center mb-2">
              {(() => {
                const id = meeting.meeting_id || meeting.id;
                const isSeenFlag = (remarksState[id]?.isSeen) ?? Boolean(meeting.is_seen);
                return isSeenFlag ? (
                  <FaCheckCircle className="text-success me-2" title="Seen" />
                ) : null;
              })()}
              <h6 className={`fw-bold ${isPast ? 'text-muted' : 'text-dark'} mb-0 me-2`}>
                {meeting.guestname + " (" + meeting.event?.title + ")"}
              </h6>
              <Badge bg={getStatusMeta(meeting.status || meeting.meeting_status || meeting.decision_status || meeting.state).variant}>
                {getStatusMeta(meeting.status || meeting.meeting_status || meeting.decision_status || meeting.state).label}
              </Badge>
            </div>

            {/* Event */}
            {meeting.event && meeting.event.title && (
              <div className="mb-2">
                <small className="fw-semibold text-dark">Event:</small>
                <small className={`ms-1 ${isPast ? 'text-muted' : 'text-dark'}`}>
                  {meeting.event.title}
                </small>
              </div>
            )}

            {/* Time */}
            <div className="d-flex align-items-center mb-2">
              <FaClock className={`me-2 ${isPast ? 'text-muted' : 'text-primary'}`} size={14} />
              <small className={isPast ? 'text-muted' : 'text-dark'}>
                {moment(meeting.starttime).format('MMMM D, YYYY h:mm A')} - {' '}
                {moment(meeting.endtime).format('h:mm A')}
              </small>
            </div>

            {/* Location */}
            {meeting.location && (
              <div className="d-flex align-items-center mb-2">
                <FaMapMarkerAlt className={`me-2 ${isPast ? 'text-muted' : 'text-danger'}`} size={14} />
                <small className={isPast ? 'text-muted' : 'text-dark'}>
                  {meeting.location}
                </small>
              </div>
            )}

            {/* Purpose */}
            <div className="mb-2">
              <small className="fw-semibold text-dark">Purpose:</small>
              <small className={`ms-1 ${isPast ? 'text-muted' : 'text-dark'}`}>
                {isPast ? meeting.additionalinfo : meeting.additionalinfo}
              </small>
            </div>

            {/* Attendees */}
            {meeting.guestemail && meeting.guestemail.length > 0 && (
              <div className="d-flex align-items-center">
                <small className="text-dark fw-semibold me-1">Attendees:</small>
                <small className="text-dark">

                  {meeting.guestemail}
                </small>
              </div>
            )}

            {/* Remarks and Seen */}
            <div className="mt-3">
              <Button
                variant="link"
                className="px-0 d-inline-flex align-items-center text-decoration-none"
                onClick={() => {
                  const id = meeting.meeting_id || meeting.id;
                  setRemarksOpen(prev => ({ ...prev, [id]: !prev[id] }));
                }}
              >
                {remarksOpen[meeting.meeting_id || meeting.id] ? <FaChevronUp className="me-1" /> : <FaChevronDown className="me-1" />}
                Remarks
              </Button>
              {remarksOpen[meeting.meeting_id || meeting.id] && (
                <div className="mt-2">
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={(remarksState[meeting.meeting_id || meeting.id]?.text) ?? meeting.remarks ?? ''}
                    onChange={(e) => handleRemarkChange(meeting, 'text', e.target.value)}
                    placeholder="Add a remark..."
                  />
                  <div className="d-flex align-items-center mt-2">
                    <Form.Check
                      type="switch"
                      id={`seen-${meeting.meeting_id || meeting.id}`}
                      label="Mark as seen"
                      checked={(remarksState[meeting.meeting_id || meeting.id]?.isSeen) ?? Boolean(meeting.is_seen)}
                      onChange={(e) => handleRemarkChange(meeting, 'isSeen', e.target.checked)}
                    />
                    <Button
                      variant="outline-primary"
                      size="sm"
                      className="ms-3"
                      onClick={() => handleRemarkSave(meeting)}
                      disabled={remarksLoading === (meeting.meeting_id || meeting.id)}
                    >
                      {remarksLoading === (meeting.meeting_id || meeting.id) ? (
                        <Spinner animation="border" size="sm" className="me-1" />
                      ) : null}
                      Save
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </Col>

          {/* Action Button */}
          <Col xs={12} lg={4} className="mt-3 mt-lg-0">
            <div className="d-flex flex-column gap-2 justify-content-lg-end align-items-stretch">
              {!isPast && meeting.status === 'PENDING_APPROVAL' && (
                <div className="d-flex gap-2">
                  <Button
                    variant="success"
                    size="sm"
                    className="w-100"
                    onClick={() => handleMeetingDecision(meeting, 'approve')}
                    disabled={decisionLoading !== null}
                  >
                    {decisionLoading === `${(meeting.meeting_id || meeting.id)}-approve` ? (
                      <Spinner animation="border" size="sm" className="me-1" />
                    ) : (
                      <FaCheck className="me-1" />
                    )}
                    Approve
                  </Button>
                  <Button
                    variant="danger"
                    size="sm"
                    className="w-100"
                    onClick={() => handleMeetingDecision(meeting, 'reject')}
                    disabled={decisionLoading !== null}
                  >
                    {decisionLoading === `${(meeting.meeting_id || meeting.id)}-reject` ? (
                      <Spinner animation="border" size="sm" className="me-1" />
                    ) : (
                      <FaTimes className="me-1" />
                    )}
                    Reject
                  </Button>
                </div>
              )}
              {/* <div className="d-flex justify-content-lg-end">
                {!isPast && meeting.meetlink ? (
                  <Button
                    variant="primary"
                    size="sm"
                    className="w-100 w-lg-auto"
                    href={meeting.meetlink}
                    target='_blank'
                  >
                    <FaVideo className="me-1" />
                    Join Meeting
                  </Button>
                ) : (
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    disabled
                    className="w-100 w-lg-auto"
                  >
                    <FaEye className="me-1" />
                    View Details
                  </Button>
                )}
              </div> */}
              {!isPast && (meeting.status || '').toUpperCase() !== 'CANCELLED' && (
                <div className="d-flex justify-content-lg-end mt-2">
                  <Button
                    variant="outline-danger"
                    size="sm"
                    className="w-100 w-lg-auto"
                    onClick={() => setCancelModalMeeting(meeting)}
                  >
                    <FaTimes className="me-1" />
                    Cancel Meeting
                  </Button>
                </div>
              )}
            </div>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );

  // --- Existing MeetingSection Component (100% preserved) ---

  const MeetingSection = ({ title, meetings, emptyMessage, isPast = false, icon }) => (
    <Card className="border-0 shadow-sm mb-4">
      <Card.Header className="bg-white border-bottom-0 pb-2">
        <div className="d-flex align-items-center">
          {icon}
          <h5 className="fw-bold text-dark mb-0 ms-2">{title}</h5>
          <Badge bg={isPast ? "secondary" : "primary"} className="ms-2">
            {meetings.length}
          </Badge>
        </div>
      </Card.Header>
      <Card.Body className="p-0">
        {meetings.length === 0 ? (
          <div className="text-center py-5">
            <FaExclamationCircle size={32} className="text-muted mb-3" />
            <p className="text-muted mb-0">{emptyMessage}</p>
          </div>
        ) : (
          <div
            style={{
              maxHeight: '500px',
              overflowY: 'auto'
            }}
            className="p-3"
          >
            {meetings.map((meeting) => (
              <MeetingCard
                key={meeting.id}
                meeting={meeting}
                isPast={isPast}
              />
            ))}
          </div>
        )}
      </Card.Body>
    </Card>
  );

  // --- Calendar Renderer ---

  const renderCalendar = () => {
    const startOfMonth = currentMonth.clone().startOf('month');
    const endOfMonth = currentMonth.clone().endOf('month');
    const daysInMonth = currentMonth.daysInMonth();
    const firstDayOfWeek = startOfMonth.day(); // 0=Sun
    const today = moment();

    // Build array of day cells
    const cells = [];

    // Previous month trailing days
    const prevMonth = startOfMonth.clone().subtract(1, 'month');
    const prevMonthDays = prevMonth.daysInMonth();
    for (let i = firstDayOfWeek - 1; i >= 0; i--) {
      const dayNum = prevMonthDays - i;
      const dateKey = prevMonth.clone().date(dayNum).format('YYYY-MM-DD');
      const dateMeetings = meetingsByDate[dateKey] || [];
      cells.push({
        dayNum,
        dateKey,
        meetings: dateMeetings,
        isCurrentMonth: false,
        isToday: false
      });
    }

    // Current month days
    for (let day = 1; day <= daysInMonth; day++) {
      const dateMoment = currentMonth.clone().date(day);
      const dateKey = dateMoment.format('YYYY-MM-DD');
      const dateMeetings = meetingsByDate[dateKey] || [];
      cells.push({
        dayNum: day,
        dateKey,
        meetings: dateMeetings,
        isCurrentMonth: true,
        isToday: dateMoment.isSame(today, 'day')
      });
    }

    // Next month leading days to fill the grid (always complete to full weeks)
    const remainder = cells.length % 7;
    if (remainder > 0) {
      const fillCount = 7 - remainder;
      for (let i = 1; i <= fillCount; i++) {
        const dateKey = endOfMonth.clone().add(i, 'days').format('YYYY-MM-DD');
        const dateMeetings = meetingsByDate[dateKey] || [];
        cells.push({
          dayNum: i,
          dateKey,
          meetings: dateMeetings,
          isCurrentMonth: false,
          isToday: false
        });
      }
    }

    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const MAX_INDICATORS = 2;

    return (
      <div className="mtg-calendar-container">
        {/* Calendar Navigation */}
        <div className="mtg-calendar-nav">
          <div className="d-flex align-items-center gap-2">
            <button className="mtg-calendar-nav-btn" onClick={goToPrevMonth} aria-label="Previous month">
              <FaChevronLeft size={12} />
            </button>
            <button className="mtg-calendar-nav-btn" onClick={goToNextMonth} aria-label="Next month">
              <FaChevronRight size={12} />
            </button>
            <h3 className="mtg-calendar-nav-title">
              {currentMonth.format('MMMM YYYY')}
            </h3>
          </div>
          <button className="mtg-calendar-today-btn" onClick={goToToday}>
            Today
          </button>
        </div>

        {/* Calendar Grid */}
        <div className="mtg-calendar-grid">
          {/* Day name headers */}
          {dayNames.map(name => (
            <div key={name} className="mtg-calendar-dayname">{name}</div>
          ))}

          {/* Day cells */}
          {cells.map((cell, idx) => {
            const hasMeetings = cell.meetings.length > 0;
            const cellClasses = [
              'mtg-calendar-day',
              !cell.isCurrentMonth && 'mtg-calendar-day--other-month',
              cell.isToday && 'mtg-calendar-day--today',
              hasMeetings && cell.isCurrentMonth && 'mtg-calendar-day--has-meetings'
            ].filter(Boolean).join(' ');

            const visibleMeetings = cell.meetings.slice(0, MAX_INDICATORS);
            const extraCount = cell.meetings.length - MAX_INDICATORS;

            return (
              <div
                key={`cell-${idx}`}
                className={cellClasses}
                onClick={() => hasMeetings && handleDateClick(cell.dateKey, cell.meetings)}
                role={hasMeetings ? 'button' : undefined}
                tabIndex={hasMeetings ? 0 : undefined}
                onKeyDown={(e) => {
                  if (hasMeetings && (e.key === 'Enter' || e.key === ' ')) {
                    e.preventDefault();
                    handleDateClick(cell.dateKey, cell.meetings);
                  }
                }}
                aria-label={hasMeetings ? `${cell.dayNum}, ${cell.meetings.length} meeting(s)` : `${cell.dayNum}`}
              >
                <div className="mtg-calendar-daynum">{cell.dayNum}</div>

                {/* Meeting indicators */}
                {cell.isCurrentMonth && visibleMeetings.map((m, mIdx) => {
                  const statusClass = getIndicatorClass(m.status || m.meeting_status || m.decision_status || m.state);
                  return (
                    <div
                      key={mIdx}
                      className={`mtg-calendar-indicator mtg-calendar-indicator--${statusClass}`}
                      title={`${m.guestname || 'Guest'} — ${moment(m.starttime).format('h:mm A')}`}
                    >
                      <span className={`mtg-calendar-indicator-dot mtg-calendar-indicator-dot--${statusClass}`} />
                      <span className="mtg-calendar-indicator-text">
                        {m.guestname || m.event?.title || 'Meeting'}
                      </span>
                      <span className="mtg-calendar-indicator-time">
                        {moment(m.starttime).format('h:mm A')}
                      </span>
                    </div>
                  );
                })}

                {/* +N more */}
                {cell.isCurrentMonth && extraCount > 0 && (
                  <div className="mtg-calendar-more">+{extraCount} more</div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // --- Loading State ---
  if (loading) {
    return (
      <Container className="py-4">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <div className="text-center">
            <Spinner animation="border" variant="primary" className="mb-3" />
            <p className="text-muted">Loading your meetings...</p>
          </div>
        </div>
      </Container>
    );
  }

  // --- Error State ---
  if (error) {
    return (
      <Container className="py-4">
        <Alert variant="danger" className="d-flex align-items-center">
          <FaExclamationCircle className="me-2" />
          {error}
        </Alert>
      </Container>
    );
  }

  // --- Main Render ---
  return (
    <Container fluid className="py-4">
      <LoadingOverlay isLoading={isOverlayLoading} />
      <Row className="justify-content-center">
        <Col xs={12} lg={11} xl={10}>
          {/* Page Header with View Toggle */}
          <div className="mtg-page-header">
            <div>
              <h2 className="mtg-page-title">Your Meetings</h2>
              <p className="mtg-page-subtitle">Manage and join your scheduled meetings</p>
            </div>
            <div className="mtg-view-toggle">
              <button
                className={`mtg-view-toggle-btn ${viewMode === 'calendar' ? 'mtg-view-toggle-btn--active' : ''}`}
                onClick={() => setViewMode('calendar')}
              >
                <FaCalendarAlt size={13} />
                Calendar
              </button>
              <button
                className={`mtg-view-toggle-btn ${viewMode === 'list' ? 'mtg-view-toggle-btn--active' : ''}`}
                onClick={() => setViewMode('list')}
              >
                <FaList size={13} />
                List
              </button>
            </div>
          </div>

          {/* Stats Bar */}
          <div className="mtg-stats-bar">
            <div className="mtg-stat-card">
              <div className="mtg-stat-icon mtg-stat-icon--total">
                <FaCalendarAlt />
              </div>
              <div>
                <div className="mtg-stat-value">{meetings.length}</div>
                <div className="mtg-stat-label">Total</div>
              </div>
            </div>
            <div className="mtg-stat-card">
              <div className="mtg-stat-icon mtg-stat-icon--upcoming">
                <FaCalendarCheck />
              </div>
              <div>
                <div className="mtg-stat-value">{upcomingMeetings.length}</div>
                <div className="mtg-stat-label">Upcoming</div>
              </div>
            </div>
            <div className="mtg-stat-card">
              <div className="mtg-stat-icon mtg-stat-icon--past">
                <FaCalendarTimes />
              </div>
              <div>
                <div className="mtg-stat-value">{pastMeetings.length}</div>
                <div className="mtg-stat-label">Past</div>
              </div>
            </div>
          </div>

          {/* ---- CALENDAR VIEW ---- */}
          {viewMode === 'calendar' && (
            <>
              {meetings.length === 0 ? (
                <div className="mtg-calendar-container">
                  <div className="mtg-calendar-empty">
                    <div className="mtg-calendar-empty-icon">📅</div>
                    <div className="mtg-calendar-empty-text">No meetings scheduled yet.</div>
                  </div>
                </div>
              ) : (
                renderCalendar()
              )}
            </>
          )}

          {/* ---- LIST VIEW (original layout) ---- */}
          {viewMode === 'list' && (
            <>
              <MeetingSection
                title="Upcoming Meetings"
                meetings={upcomingMeetings}
                emptyMessage="No upcoming meetings scheduled."
                icon={<FaCalendarCheck className="text-primary" size={20} />}
              />
              <MeetingSection
                title="Past Meetings"
                meetings={pastMeetings}
                emptyMessage="No past meetings found."
                isPast={true}
                icon={<FaCalendarTimes className="text-secondary" size={20} />}
              />
            </>
          )}
        </Col>
      </Row>

      {/* ---- Day Detail Modal ---- */}
      <Modal
        show={showDayModal}
        onHide={closeDayModal}
        centered
        size="lg"
        className="mtg-day-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {selectedDateLabel}
            <span className="mtg-day-modal-count">{selectedDateMeetings.length}</span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedDateMeetings.map((meeting) => {
            const isPast = moment(meeting.starttime).isBefore(moment());
            return (
              <MeetingCard
                key={meeting.meeting_id || meeting.id}
                meeting={meeting}
                isPast={isPast}
              />
            );
          })}
        </Modal.Body>
      </Modal>

      {/* Cancel Confirmation Modal (preserved) */}
      <Modal show={!!cancelModalMeeting} onHide={() => !cancelLoading && setCancelModalMeeting(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Cancel Meeting</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to cancel this meeting
          {cancelModalMeeting?.guestname ? ` with ${cancelModalMeeting.guestname}` : ''}
          {cancelModalMeeting?.starttime
            ? ` on ${moment(cancelModalMeeting.starttime).format('MMMM D, YYYY h:mm A')}`
            : ''}?
          This action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setCancelModalMeeting(null)} disabled={cancelLoading}>
            No, Keep It
          </Button>
          <Button variant="danger" onClick={handleCancelMeeting} disabled={cancelLoading}>
            {cancelLoading ? (
              <><Spinner animation="border" size="sm" className="me-1" /> Cancelling...</>
            ) : (
              'Yes, Cancel Meeting'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Custom Styles (preserved) */}
      <style>{`
        .card {
          border-radius: 12px;
        }
        
        .scrollable-container {
          scrollbar-width: thin;
          scrollbar-color: #bdbdbd #f1f1f1;
        }
        
        .scrollable-container::-webkit-scrollbar {
          width: 6px;
        }
        
        .scrollable-container::-webkit-scrollbar-thumb {
          background-color: #bdbdbd;
          border-radius: 4px;
        }
        
        .scrollable-container::-webkit-scrollbar-track {
          background-color: #f1f1f1;
          border-radius: 4px;
        }
        
        @media (max-width: 768px) {
          .container-fluid {
            padding-left: 15px;
            padding-right: 15px;
          }
        }
      `}</style>

    </Container>
  );
};

export default MeetingList;
